// Escolha do CADASTRO de professor (club_teachers) ao dar acesso de professor
// a um login (club_staff role='teacher').
//
// Por que existe (PROD, Arena Figueira, 16/10/2026): o professor só vê as aulas
// cujo `teacher_id` é o cadastro LIGADO ao login dele. Os cadastros tinham sido
// criados antes, sem login; os logins vieram depois por Equipe → Adicionar
// membro, que não ligava nada — e os professores viam a lista de aulas vazia,
// sem erro. A ligação é gravada pela RPC `link_teacher_login`; aqui fica a
// regra da TELA (o que sugerir, quando exigir escolha), pura e testada em
// `vinculo-professor.test.ts`.

/** Valor do seletor para "criar um cadastro novo para este login". */
export const NOVO_CADASTRO = '__novo__';

export interface CadastroDeProfessor {
  id: string;
  club_id: string;
  name: string;
  email: string | null;
  user_id: string | null;
  is_active: boolean;
}

/** Cadastros do clube que podem receber um login: ativos e ainda sem login. */
export function cadastrosSemLogin<T extends CadastroDeProfessor>(cadastros: T[], clubId: string): T[] {
  return cadastros
    .filter(c => c.club_id === clubId && c.is_active && !c.user_id)
    .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
}

const normalizarEmail = (e: string | null | undefined) => (e ?? '').trim().toLowerCase();

/**
 * Escolha inicial do seletor:
 *   - o único cadastro sem login com o mesmo e-mail (é a mesma pessoa);
 *   - "cadastro novo" quando o clube não tem cadastro sem login;
 *   - '' (o gestor TEM de escolher) quando há cadastros sem login e nenhum casa
 *     pelo e-mail — o caso da Figueira, em que criar um cadastro novo deixaria o
 *     professor de novo sem as aulas.
 */
export function escolhaInicial(semLogin: CadastroDeProfessor[], email: string | null | undefined): string {
  const alvo = normalizarEmail(email);
  if (alvo) {
    const iguais = semLogin.filter(c => normalizarEmail(c.email) === alvo);
    if (iguais.length === 1) return iguais[0].id;
  }
  return semLogin.length === 0 ? NOVO_CADASTRO : '';
}

/**
 * O seletor segue a sugestão ENQUANTO o gestor não escolheu à mão: o valor
 * atual vazio ou igual à sugestão anterior é "automático" e acompanha a nova
 * sugestão — inclusive voltando a '' quando o e-mail corrigido deixa de casar.
 * Escolha manual nunca é sobrescrita. Devolve o valor novo, ou null para manter.
 */
export function proximaEscolha(atual: string, sugestaoAnterior: string, sugestao: string): string | null {
  const automatico = atual === '' || atual === sugestaoAnterior;
  return automatico && atual !== sugestao ? sugestao : null;
}

/** A escolha está completa? Cadastro novo exige nome. */
export function escolhaCompleta(escolha: string, nomeNovo: string): boolean {
  if (escolha === NOVO_CADASTRO) return nomeNovo.trim().length > 0;
  return escolha.length > 0;
}

/** Argumentos da ligação (edge `provision-staff-user` e RPC `link_teacher_login`). */
export function argumentosDaEscolha(escolha: string, nomeNovo: string): { teacherId: string | null; teacherName: string | null } {
  if (escolha === NOVO_CADASTRO) return { teacherId: null, teacherName: nomeNovo.trim() || null };
  return { teacherId: escolha || null, teacherName: null };
}

/**
 * Motivos de recusa da RPC `link_teacher_login`. Espelho de
 * `MOTIVOS_DO_VINCULO` em `supabase/functions/_shared/teacher-login-link.ts`
 * (o CRM não importa de `supabase/functions/`); o teste de contrato compara as
 * duas listas e exige texto em pt e en para cada motivo.
 */
export const MOTIVOS_DO_VINCULO = [
  'teacher_name_taken',
  'teacher_already_has_login',
  'teacher_not_found',
  'teacher_name_required',
  'teacher_login_not_found',
  'self_link_forbidden',
] as const;

export type MotivoDoVinculo = (typeof MOTIVOS_DO_VINCULO)[number];

export function motivoDoVinculo(texto: string | null | undefined): MotivoDoVinculo | null {
  if (!texto) return null;
  return MOTIVOS_DO_VINCULO.find(m => texto.includes(m)) ?? null;
}

export const chaveDoMotivo = (m: MotivoDoVinculo) => `teachers.link.reason.${m}`;
